import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import AtomicButton from '../../atomic/button/Button';

const NOTIFICATION_TYPES = [
  { value: 'order', label: 'Order' },
  { value: 'ride', label: 'Ride' },
  { value: 'settings', label: 'Settings' },
  { value: 'profile', label: 'Profile' },
  { value: 'info', label: 'Info' },
];

const EXTRA_DATA_PLACEHOLDER = `{
  "order_id": "ORD-12345",
  "screen": "OrderDetails"
}`;

export function SendNotificationModal({ open, user, onClose, onSend, sending }) {
  const [type, setType] = useState('info');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [image, setImage] = useState('');
  const [rideId, setRideId] = useState('');
  const [extraDataRaw, setExtraDataRaw] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setType('info');
      setTitle('');
      setDescription('');
      setImage('');
      setRideId('');
      setExtraDataRaw('');
      setError('');
    }
  }, [open, user]);

  const recipientLabel =
    user?.account_type === 'kitchen'
      ? user?.kitchen_name || 'Kitchen'
      : user?.email || user?.user_id || user?.device_id || 'Selected user';

  const handleSubmit = async () => {
    if (!type) {
      setError('Notification type is required.');
      return;
    }
    if (!title.trim()) {
      setError('Title is required.');
      return;
    }
    if (title.trim().length > 200) {
      setError('Title must be 200 characters or less.');
      return;
    }
    if (!description.trim()) {
      setError('Description is required.');
      return;
    }
    if (description.trim().length > 1000) {
      setError('Description must be 1000 characters or less.');
      return;
    }

    let extra_data = {};
    if (extraDataRaw.trim()) {
      try {
        const parsed = JSON.parse(extraDataRaw.trim());
        if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
          setError('Extra data must be a JSON object.');
          return;
        }
        extra_data = parsed;
      } catch {
        setError('Extra data must be valid JSON.');
        return;
      }
    }

    if (type === 'ride') {
      const ride_id = rideId.trim() || extra_data.ride_id;
      if (!ride_id) {
        setError('Ride ID is required for ride notifications.');
        return;
      }
      extra_data = { ...extra_data, ride_id };
    }

    setError('');
    await onSend({
      type,
      title: title.trim(),
      description: description.trim(),
      ...(image.trim() ? { image: image.trim() } : {}),
      ...(Object.keys(extra_data).length > 0 ? { extra_data } : {}),
    });
  };

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontFamily: 'Inter Bold', color: '#091057' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NotificationsActiveOutlinedIcon sx={{ color: '#EC8305' }} />
          Send Push Notification
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2, color: '#537D5D', fontFamily: 'Inter Regular' }}>
          Recipient: <strong>{recipientLabel}</strong>
          {user?.account_type ? ` (${user.account_type})` : ''}
        </Typography>

        {user?.permission === false && (
          <Typography variant="body2" sx={{ mb: 2, color: '#d97706' }}>
            This device has not granted notification permission.
          </Typography>
        )}

        <FormControl fullWidth margin="normal" size="small" disabled={sending}>
          <InputLabel id="notification-type-label">Type</InputLabel>
          <Select
            labelId="notification-type-label"
            value={type}
            label="Type"
            onChange={(e) => setType(e.target.value)}
          >
            {NOTIFICATION_TYPES.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          margin="normal"
          size="small"
          inputProps={{ maxLength: 200 }}
          disabled={sending}
          helperText={`${title.length}/200`}
        />

        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          margin="normal"
          size="small"
          multiline
          minRows={3}
          inputProps={{ maxLength: 1000 }}
          disabled={sending}
          helperText={`${description.length}/1000`}
        />

        <TextField
          label="Image URL (optional)"
          value={image}
          onChange={(e) => setImage(e.target.value)}
          fullWidth
          margin="normal"
          size="small"
          placeholder="https://example.com/image.png"
          disabled={sending}
        />

        {type === 'ride' && (
          <TextField
            label="Ride ID"
            value={rideId}
            onChange={(e) => setRideId(e.target.value)}
            fullWidth
            margin="normal"
            size="small"
            required
            disabled={sending}
            helperText="Required for ride notifications"
          />
        )}

        <TextField
          label="Extra data (optional JSON)"
          value={extraDataRaw}
          onChange={(e) => setExtraDataRaw(e.target.value)}
          fullWidth
          margin="normal"
          size="small"
          multiline
          minRows={4}
          placeholder={EXTRA_DATA_PLACEHOLDER}
          disabled={sending}
          helperText="Deep link payload: order_id, screen, promo_code, etc."
          sx={{ '& textarea': { fontFamily: 'monospace', fontSize: 12 } }}
        />

        {error ? (
          <Typography variant="body2" sx={{ mt: 1, color: '#dc2626' }}>
            {error}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <AtomicButton
          label="Cancel"
          variant="outlined"
          handleSubmit={onClose}
          disabled={sending}
          style={{
            backgroundColor: 'transparent',
            color: '#091057',
            border: '1px solid #091057',
            width: '120px',
            marginTop: 0,
            marginBottom: 0,
          }}
        />
        <AtomicButton
          label="Send"
          handleSubmit={handleSubmit}
          loading={sending}
          disabled={sending || user?.permission === false}
          style={{ width: '120px', marginTop: 0, marginBottom: 0 }}
        />
      </DialogActions>
    </Dialog>
  );
}
